import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Edit, Trash2, Check, X} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {useServices} from '@/services/context'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {permissions} from '@/lib/permissions'
import {formatTimestamp} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'

export function ApprovalPolicyDetail() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.approvalPolicies.detailPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    const canUpdate = hasPermission(permissions.approvalPolicies.update)
    const canDelete = hasPermission(permissions.approvalPolicies.delete)

    const deleteDialog = useDialog()

    const {data: policy, isLoading, error} = useQuery({
        queryKey: ['approval-policy', id],
        queryFn: () => services.approvalPolicies.detail(id!),
        enabled: !!id,
    })

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.approvalPolicies.remove(id!),
        invalidate: ['approval-policies'],
        successMessage: i18n.approvalPolicies.policyDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.approvalPolicies.list())
        },
    })

    return (
        <PageLayout
            variant="detail"
            title={policy?.name ?? i18n.approvalPolicies.pageTitle}
            icon={CONCEPT_ICON.approvalPolicy}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(policy, 'APPROVAL_POLICY') : undefined)}
            data-testid="approval-policy-detail-page"
            description={policy ? (
                <InlineRow>
                    <Badge variant={policy.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                        {policy.active ? i18n.common.active : i18n.common.inactive}
                    </Badge>
                    {policy.isDefault && (
                        <Badge variant="secondary">{i18n.approvalPolicies.defaultBadge}</Badge>
                    )}
                    <Badge variant="outline">
                        {i18n.approvalPolicies.priority}: {policy.priority}
                    </Badge>
                </InlineRow>
            ) : undefined}
            actions={policy ? (
                <InlineRow>
                    {canUpdate && !policy.isDefault && (
                        <Button onClick={() => navigate(routes.accessGovernance.approvalPolicies.edit(id!))}
                                data-testid="btn-edit">
                            <IconText icon={Edit}>{i18n.common.edit}</IconText>
                        </Button>
                    )}
                    {canDelete && !policy.isDefault && (
                        <Button variant="destructive" onClick={deleteDialog.open} data-testid="btn-delete">
                            <IconText icon={Trash2}>{i18n.common.delete}</IconText>
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {policy && <>
                {/* Policy Details */}
                <DetailsCard
                    title={i18n.approvalPolicies.policyDetails}
                    description={i18n.approvalPolicies.policyDetailsDesc}
                    columns={3}
                    data-testid="card-policy-details"
                    fields={[
                        ...(policy.description ? [{label: i18n.approvalPolicies.description, value: policy.description, fullWidth: true as const}] : []),
                        {
                            label: i18n.approvalPolicies.policyType,
                            value: <Badge variant="outline" data-testid="badge-policy-type">{i18n.approvalPolicies.policyTypeLabels[policy.policyType]}</Badge>,
                        },
                        {label: i18n.approvalPolicies.priority, value: String(policy.priority)},
                        {
                            label: i18n.approvalPolicies.active,
                            value: policy.active
                                ? <IconText icon={Check} iconClassName="text-status-success">{i18n.common.yes}</IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground">{i18n.common.no}</IconText>,
                        },
                        {
                            label: i18n.approvalPolicies.isDefault,
                            value: policy.isDefault
                                ? <IconText icon={Check} iconClassName="text-status-success">{i18n.common.yes}</IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground">{i18n.common.no}</IconText>,
                        },
                        {
                            label: 'Allow proxy approval',
                            value: policy.allowProxyApproval
                                ? <IconText icon={Check} iconClassName="text-status-success">{i18n.common.yes}</IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground">{i18n.common.no}</IconText>,
                        },
                        ...(policy.validFrom ? [{label: i18n.approvalPolicies.validFrom, value: formatTimestamp(policy.validFrom)}] : []),
                        ...(policy.validTo ? [{label: i18n.approvalPolicies.validTo, value: formatTimestamp(policy.validTo)}] : []),
                        {label: i18n.approvalPolicies.createdAt, value: formatTimestamp(policy.createdAt)},
                        {label: i18n.approvalPolicies.updatedAt, value: formatTimestamp(policy.updatedAt)},
                    ]}
                />

                {/* Conditions */}
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-policy-conditions">{i18n.approvalPolicies.conditions}</CardTitle>
                        <CardDescription>
                            {i18n.approvalPolicies.conditionsCount({count: policy.conditions.length})}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {policy.conditions.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-field">{i18n.approvalPolicies.field}</TableHead>
                                        <TableHead data-testid="col-operator">{i18n.approvalPolicies.operator}</TableHead>
                                        <TableHead data-testid="col-value">{i18n.approvalPolicies.value}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {policy.conditions.map((condition) => (
                                        <TableRow key={condition.id}>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {i18n.approvalPolicies.fieldLabels[condition.field]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {i18n.approvalPolicies.operatorLabels[condition.operator]}
                                            </TableCell>
                                            <TableCell className="font-mono">{condition.value}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered className="py-4"><MutedText>{i18n.approvalPolicies.noConditions}</MutedText></MutedText>
                        )}
                    </CardContent>
                </Card>

                {/* Steps */}
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-policy-steps">{i18n.approvalPolicies.steps}</CardTitle>
                        <CardDescription>
                            {i18n.approvalPolicies.stepsCount({count: policy.steps.length})}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-16" data-testid="col-step-order">{i18n.approvalPolicies.stepOrder}</TableHead>
                                    <TableHead data-testid="col-resolver-type">{i18n.approvalPolicies.resolverType}</TableHead>
                                    <TableHead data-testid="col-resolver-param">{i18n.approvalPolicies.resolverParam}</TableHead>
                                    <TableHead data-testid="col-approval-strategy">{i18n.approvalPolicies.approvalStrategy}</TableHead>
                                    <TableHead data-testid="col-required">{i18n.approvalPolicies.required}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {policy.steps.map((step) => (
                                    <TableRow key={step.id}>
                                        <TableCell className="font-mono">{step.stepOrder}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {i18n.approvalPolicies.resolverTypeLabels[step.resolverType]}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="font-mono">
                                            {step.resolverParam || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {i18n.approvalPolicies.strategyLabels[step.approvalStrategy]}
                                        </TableCell>
                                        <TableCell>
                                            {step.required ? (
                                                <Icon icon={Check} size="md" className="text-status-success"/>
                                            ) : (
                                                <Icon icon={X} size="md" muted/>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.approvalPolicies.deletePolicy}</DialogTitle>
                        <DialogDescription>
                            {i18n.approvalPolicies.deleteConfirm({name: policy.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
